import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { reportSpamRequest, runMailMoves, moveErrorMessage } from '../utils/reportSpam';
import { moveMailRequest } from '../utils/moveMail';
import MoveToFolderButton from './MoveToFolderButton';
import ConfirmationModal from './ConfirmationModal';
import AlertModal from './AlertModal';
import MailListRow from './MailListRow';
import MailReadingPane from './MailReadingPane';
import './Sent.css';
import './Inbox.css';
import './FolderView.css';

/**
 * Sent Component
 * Displays sent mails for the logged-in user
 * Gmail-style layout
 */
const Sent = ({ currentUser }) => {
  const [mails, setMails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMail, setSelectedMail] = useState(null);
  const [selectedMailIds, setSelectedMailIds] = useState([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const handleBulkMarkUnread = async () => {
    if (selectedMailIds.length === 0) return;
    try {
      setMails(mails.map(m => 
        selectedMailIds.includes(m._id) ? { ...m, isRead: false } : m
      ));
      setSelectedMailIds([]);
    } catch (error) {
      console.error('Error marking mails as unread:', error);
    }
  };

  const handleBulkMarkSpam = async () => {
    const targets = mails.filter((mail) => selectedMailIds.includes(mail._id));
    if (targets.length === 0) return;

    try {
      const { moved, failed } = await runMailMoves(targets, reportSpamRequest);
      if (moved.length) {
        const movedIds = new Set(moved.map((mail) => mail._id));
        setMails((prev) => prev.filter((mail) => !movedIds.has(mail._id)));
        setSelectedMailIds((prev) => prev.filter((id) => !movedIds.has(id)));
        if (selectedMail && movedIds.has(selectedMail._id)) setSelectedMail(null);
        window.dispatchEvent(new CustomEvent('inboxUpdated'));
      }
      if (failed.length) {
        setAlertMessage(moveErrorMessage(failed, 'Could not report spam. Please try again.'));
        setShowAlert(true);
      }
    } catch (error) {
      console.error('Error reporting spam:', error);
      setAlertMessage('Could not report spam. Please try again.');
      setShowAlert(true);
    }
  };

  const handleMoveToFolder = async (destination) => {
    const targets = mails.filter((mail) => selectedMailIds.includes(mail._id));
    if (!targets.length) return;

    try {
      const { moved, failed } = await runMailMoves(
        targets,
        (mail) => moveMailRequest(mail, destination, 'sent')
      );
      if (moved.length) {
        const movedIds = new Set(moved.map((mail) => mail._id));
        setMails((prev) => prev.filter((mail) => !movedIds.has(mail._id)));
        setSelectedMailIds((prev) => prev.filter((id) => !movedIds.has(id)));
        if (selectedMail && movedIds.has(selectedMail._id)) setSelectedMail(null);
        window.dispatchEvent(new CustomEvent('inboxUpdated'));
      }
      if (failed.length) {
        setAlertMessage(moveErrorMessage(failed, 'Could not move mail. Please try again.'));
        setShowAlert(true);
      }
    } catch (error) {
      console.error('Error moving mail:', error);
      setAlertMessage('Could not move mail. Please try again.');
      setShowAlert(true);
    }
  };

  const handleBulkDownload = () => {
    if (selectedMailIds.length === 0) return;
    const selectedMails = mails.filter(m => selectedMailIds.includes(m._id));
    selectedMails.forEach(mail => {
      const mailContent = `From: ${mail.from}\nTo: ${mail.to}\nSubject: ${mail.subject}\nDate: ${formatFullDate(mail.createdAt)}\n\n${mail.body}`;
      const blob = new Blob([mailContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${mail.subject || 'mail'}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  };

  useEffect(() => {
    if (currentUser) {
      fetchSent();
      
      // Auto-refresh sent mails every 30 seconds (without showing loading spinner)
      const interval = setInterval(() => {
        fetchSent(false);
      }, 30000); // 30 seconds
      
      return () => clearInterval(interval);
    }
  }, [currentUser]);

  const fetchSent = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    setError('');
    try {
      const response = await axiosInstance.get('/api/mail/sent');
      if (response.data.success) {
        setMails(response.data.data);
      } else {
        setError(response.data.message || 'Failed to fetch sent mails');
      }
    } catch (error) {
      console.error('Error fetching sent mails:', error);
      setError(error.response?.data?.message || 'Error fetching sent mails. Please try again.');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleMailClick = (mail) => {
    setSelectedMail(mail);
  };

  const handleCloseMail = () => {
    setSelectedMail(null);
  };

  const formatFullDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="inbox-container">
        <div className="loading">Loading sent mails...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="inbox-container">
        <div className="error-message">{error}</div>
        <button onClick={fetchSent} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  const handleSelectMail = (mailId) => {
    setSelectedMailIds(prev => 
      prev.includes(mailId) 
        ? prev.filter(id => id !== mailId)
        : [...prev, mailId]
    );
  };

  const handleSelectAll = () => {
    if (selectedMailIds.length === mails.length) {
      setSelectedMailIds([]);
    } else {
      setSelectedMailIds(mails.map(mail => mail._id));
    }
  };

  const handleDeselectAll = () => {
    setSelectedMailIds([]);
  };

  const handleBulkDelete = async () => {
    if (selectedMailIds.length === 0) {
      setAlertMessage('Please select at least one mail to delete');
      setShowAlert(true);
      return;
    }
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    if (selectedMailIds.length === 0) return;
    
    setShowBulkDeleteConfirm(false); // Close modal immediately

    try {
      const deletePromises = selectedMailIds.map(id => 
        axiosInstance.patch(`/api/mail/move-to-trash/${id}`)
      );
      await Promise.all(deletePromises);
      
      setMails(mails.filter(m => !selectedMailIds.includes(m._id)));
      if (selectedMail && selectedMailIds.includes(selectedMail._id)) {
        setSelectedMail(null);
      }
      setSelectedMailIds([]);
      fetchSent(); // Refresh the list
    } catch (error) {
      console.error('Error deleting mails:', error);
      setAlertMessage('Error deleting mails. Please try again.');
      setShowAlert(true);
    }
  };

  // Component to display authenticated images
  const AuthImage = ({ url, alt, token }) => {
    const [imageSrc, setImageSrc] = useState(null);
    const [imageError, setImageError] = useState(false);

    useEffect(() => {
      let objectUrl = null;
      let cancelled = false;

      const loadImage = async () => {
        try {
          const response = await fetch(url, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          if (cancelled) return;
          if (response.ok) {
            const blob = await response.blob();
            if (cancelled) return;
            objectUrl = URL.createObjectURL(blob);
            if (cancelled) {
              URL.revokeObjectURL(objectUrl);
              objectUrl = null;
              return;
            }
            setImageSrc(objectUrl);
          } else {
            setImageError(true);
          }
        } catch (error) {
          if (!cancelled) {
            console.error('Error loading image:', error);
            setImageError(true);
          }
        }
      };

      loadImage();

      return () => {
        cancelled = true;
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
      };
    }, [url, token]);

    if (imageError) {
      return (
        <div className="image-load-error" style={{display: 'flex'}}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" opacity="0.3">
            <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
          </svg>
          <p>Unable to load image</p>
        </div>
      );
    }

    if (!imageSrc) {
      return (
        <div style={{padding: '40px', textAlign: 'center', color: '#5f6368'}}>
          <p>Loading image...</p>
        </div>
      );
    }

    return <img src={imageSrc} alt={alt} style={{width: '100%', height: 'auto', maxHeight: '400px', objectFit: 'contain', display: 'block'}} />;
  };

  return (
    <div className={`inbox-container premium-mail-dashboard ${selectedMail ? 'mail-detail-open' : ''}`}>
      <div className="inbox-header">
        <div>
          <p className="inbox-eyebrow">ATOZASMAIL Workspace</p>
          <h2>Sent</h2>
        </div>
        <div className="inbox-header-actions">
          <p className="mail-count">{mails.length} {mails.length === 1 ? 'mail' : 'mails'}</p>
        </div>
      </div>

      <div className={`inbox-content reading-pane-no-split gmail-split-layout${selectedMail ? ' has-detail-open full-detail-view' : ''}`}>
        <div className={`mail-list-container ${selectedMail ? 'hidden' : 'full-width'}`}>
          {mails.length > 0 && (
            <div className="mail-toolbar">
              <div className="toolbar-left">
                <button 
                  className="toolbar-btn checkbox-btn"
                  onClick={selectedMailIds.length > 0 ? handleDeselectAll : handleSelectAll}
                  title={selectedMailIds.length > 0 ? "Deselect all" : "Select all"}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    {selectedMailIds.length > 0 && (
                      <line x1="7" y1="12" x2="17" y2="12" strokeLinecap="round"/>
                    )}
                  </svg>
                  {selectedMailIds.length > 0 && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" className="dropdown-icon">
                      <path d="M7 10l5 5 5-5z"/>
                    </svg>
                  )}
                </button>
                
                <button 
                  className="toolbar-btn"
                  onClick={fetchSent}
                  title="Refresh"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 4v6h-6"/>
                    <path d="M1 20v-6h6"/>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                  </svg>
                </button>
                
                {selectedMailIds.length > 0 && (
                  <>
                    <button 
                      className="toolbar-btn"
                      onClick={handleBulkDownload}
                      title="Download"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                    </button>
                    
                    <button 
                      className="toolbar-btn"
                      onClick={handleBulkMarkSpam}
                      title="Report spam"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2"/>
                        <line x1="12" y1="8" x2="12" y2="12"/>
                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                    </button>
                    
                    <button 
                      className="toolbar-btn"
                      onClick={handleBulkDelete}
                      title="Delete"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                      </svg>
                    </button>
                  </>
                )}
              </div>
              
              {selectedMailIds.length > 0 && (
                <>
                  <div className="toolbar-separator"></div>
                  <div className="toolbar-right">
                    <button 
                      className="toolbar-btn"
                      onClick={handleBulkMarkUnread}
                      title="Mark as unread"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                      <circle cx="18" cy="4" r="3" fill="currentColor" className="notification-dot"/>
                    </button>
                    
                    <MoveToFolderButton currentFolder="sent" onMove={handleMoveToFolder} />
                    
                    <button 
                      className="toolbar-btn"
                      title="More options"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="5" r="2"/>
                        <circle cx="12" cy="12" r="2"/>
                        <circle cx="12" cy="19" r="2"/>
                      </svg>
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          <div className="mail-list">
            {mails.length === 0 ? (
              <div className="empty-sent">
                <p>No sent mails</p>
                <p className="empty-hint">Compose a mail to get started!</p>
              </div>
            ) : (
              mails.map((mail) => (
                <MailListRow
                  key={mail._id}
                  mail={mail}
                  mailKey={mail._id}
                  avatarSeed={mail.to}
                  senderText={`To: ${mail.to}`}
                  isChecked={selectedMailIds.includes(mail._id)}
                  isOpen={selectedMail?._id === mail._id}
                  isUnread={false}
                  onOpen={handleMailClick}
                  onToggleCheck={handleSelectMail}
                />
              ))
            )}
          </div>
        </div>

        {selectedMail && (
          <MailReadingPane
            mail={selectedMail}
            folderLabel="Sent"
            onClose={handleCloseMail}
          >
              {selectedMail.attachments && selectedMail.attachments.length > 0 && (
                <div className="mail-attachments-section">
                  <div className="attachments-header">Attachments ({selectedMail.attachments.length})</div>
                  <div className="attachments-list">
                    {selectedMail.attachments.map((attachment, index) => {
                      const formatFileSize = (bytes) => {
                        if (bytes === 0) return '0 Bytes';
                        const k = 1024;
                        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                        const i = Math.floor(Math.log(bytes) / Math.log(k));
                        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
                      };
                      
                      const isImage = attachment.mimetype && attachment.mimetype.startsWith('image/');
                      const token = localStorage.getItem('token');
                      const attachmentUrl = `http://localhost:5000/api/mail/${selectedMail._id}/attachment/${index}`;
                      
                      return (
                        <div key={index} className="attachment-container-detail">
                          <div className="attachment-item-detail">
                          <div className="attachment-icon-wrapper">
                            {isImage ? (
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                              </svg>
                            ) : (
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"/>
                              </svg>
                            )}
                          </div>
                          <div className="attachment-info-detail">
                            <div className="attachment-name-detail">{attachment.filename}</div>
                            <div className="attachment-size-detail">{formatFileSize(attachment.size)}</div>
                          </div>
                            <a 
                              href={attachmentUrl}
                              download={attachment.filename}
                              className="attachment-download-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                fetch(attachmentUrl, {
                                  headers: {
                                    'Authorization': `Bearer ${token}`
                                  }
                                })
                                .then(res => res.blob())
                                .then(blob => {
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = attachment.filename;
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  document.body.removeChild(a);
                                });
                              }}
                              title="Download"
                            >
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                              </svg>
                            </a>
                          </div>
                          {isImage && (
                            <div className="attachment-image-preview">
                              <AuthImage url={attachmentUrl} alt={attachment.filename} token={token} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
          </MailReadingPane>
        )}
      </div>

      <ConfirmationModal
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={confirmBulkDelete}
        message={`Are you sure you want to delete ${selectedMailIds.length} mail(s)?`}
        confirmText="OK"
        cancelText="Cancel"
      />

      <AlertModal
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        message={alertMessage}
      />
    </div>
  );
};

export default Sent;

